import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../components/Input';
import Button from '../components/Button';
import FormGroup from '../components/FormGroup';
import { useFormData } from '../context/FormDataContext';
import { Plus, Minus, CreditCard } from 'lucide-react';
import { stripeProducts } from '../stripe-config';

const TicketPurchase = () => {
  const navigate = useNavigate();
  const { setFormData } = useFormData();
  
  const [formState, setFormState] = useState({
    fullName: '',
    email: '',
    phone: '',
    adultQuantity: 0,
    childQuantity: 0,
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleQuantityChange = (type: 'adult' | 'child', amount: number) => {
    const field = type === 'adult' ? 'adultQuantity' : 'childQuantity';
    
    setFormState((prev) => ({
      ...prev,
      [field]: Math.max(0, prev[field] + amount),
    }));
    
    // Clear error when quantity is changed
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formState.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formState.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formState.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number should be 10 digits';
    }
    
    if (formState.adultQuantity === 0 && formState.childQuantity === 0) {
      newErrors.adultQuantity = 'Please select at least one ticket';
      newErrors.childQuantity = 'Please select at least one ticket';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Calculate total amount
      const adultTotal = stripeProducts.adultTicket.price * formState.adultQuantity;
      const childTotal = stripeProducts.childTicket.price * formState.childQuantity;
      const totalAmount = adultTotal + childTotal;
      
      // Generate a mock confirmation number
      const confirmationNumber = Math.floor(1000000 + Math.random() * 9000000).toString();
      
      // Store form data in context for confirmation page
      setFormData({
        type: 'ticket',
        ...formState,
        ticketType: 'multiple',
        totalAmount,
        id: 'mock-id-' + Math.random().toString(36).substring(2, 11),
        confirmationNumber
      });
      
      // Navigate to confirmation page
      navigate('/confirmation/ticket');
    } catch (error) {
      console.error('Error processing purchase:', error);
      setIsSubmitting(false);
    }
  };
  
  const adultTotal = stripeProducts.adultTicket.price * formState.adultQuantity;
  const childTotal = stripeProducts.childTicket.price * formState.childQuantity;
  const totalAmount = adultTotal + childTotal;
  
  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary-900 mb-6">
        Purchase Tickets
      </h1>
      
      <p className="text-gray-700 mb-8">
        Save time at the gate and secure your spot at our exciting rodeo event. Get tickets for you and your family!
      </p>
      
      <form onSubmit={handleSubmit}>
        <FormGroup title="Contact Information" description="Your ticket confirmation will be sent to this email.">
          <Input
            label="Full Name"
            id="fullName"
            name="fullName"
            placeholder="Enter your full name"
            value={formState.fullName}
            onChange={handleChange}
            required
            error={errors.fullName}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Email"
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email address"
              value={formState.email}
              onChange={handleChange}
              required
              error={errors.email}
              helper="We'll send your tickets to this email"
            />
            
            <Input
              label="Phone Number"
              id="phone"
              name="phone"
              type="tel"
              placeholder="Enter your phone number"
              value={formState.phone}
              onChange={handleChange}
              required
              error={errors.phone}
            />
          </div>
        </FormGroup>
        
        <FormGroup title="Ticket Selection" description="Choose the quantity of tickets you would like to purchase.">
          <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h3 className="font-semibold text-primary-800">{stripeProducts.adultTicket.name}</h3>
                  <p className="text-sm text-gray-600">{stripeProducts.adultTicket.description}</p>
                </div>
                <div className="text-lg font-semibold text-primary-700">
                  ${stripeProducts.adultTicket.price.toFixed(2)}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Button
                    type="button"
                    variant="outline"
                    className="p-2"
                    onClick={() => handleQuantityChange('adult', -1)}
                    disabled={formState.adultQuantity <= 0}
                  >
                    <Minus size={16} />
                  </Button>
                  <span className="mx-4 font-medium text-lg min-w-[40px] text-center">
                    {formState.adultQuantity}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    className="p-2"
                    onClick={() => handleQuantityChange('adult', 1)}
                  >
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="font-medium">
                  ${adultTotal.toFixed(2)}
                </div>
              </div>
              
              {errors.adultQuantity && (
                <p className="mt-1 text-sm text-accent-600">{errors.adultQuantity}</p>
              )}
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h3 className="font-semibold text-primary-800">{stripeProducts.childTicket.name}</h3>
                  <p className="text-sm text-gray-600">{stripeProducts.childTicket.description}</p>
                </div>
                <div className="text-lg font-semibold text-primary-700">
                  ${stripeProducts.childTicket.price.toFixed(2)}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Button
                    type="button"
                    variant="outline"
                    className="p-2"
                    onClick={() => handleQuantityChange('child', -1)}
                    disabled={formState.childQuantity <= 0}
                  >
                    <Minus size={16} />
                  </Button>
                  <span className="mx-4 font-medium text-lg min-w-[40px] text-center">
                    {formState.childQuantity}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    className="p-2"
                    onClick={() => handleQuantityChange('child', 1)}
                  >
                    <Plus size={16} />
                  </Button>
                </div>
                <div className="font-medium">
                  ${childTotal.toFixed(2)}
                </div>
              </div>
              
              {errors.childQuantity && (
                <p className="mt-1 text-sm text-accent-600">{errors.childQuantity}</p>
              )}
            </div>
          </div>
        </FormGroup>
        
        {(formState.adultQuantity > 0 || formState.childQuantity > 0) && (
          <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
            <h3 className="text-xl font-heading font-semibold text-primary-800 mb-4">
              Order Summary
            </h3>
            
            <div className="space-y-2 mb-4">
              {formState.adultQuantity > 0 && (
                <div className="flex justify-between">
                  <span>{stripeProducts.adultTicket.name} (x{formState.adultQuantity})</span>
                  <span>${adultTotal.toFixed(2)}</span>
                </div>
              )}
              
              {formState.childQuantity > 0 && (
                <div className="flex justify-between">
                  <span>{stripeProducts.childTicket.name} (x{formState.childQuantity})</span>
                  <span>${childTotal.toFixed(2)}</span>
                </div>
              )}
              
              <div className="h-px bg-gray-200 my-2"></div>
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-end gap-4 mt-8 mb-12">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/')}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="secondary"
            disabled={isSubmitting || (formState.adultQuantity === 0 && formState.childQuantity === 0)}
            icon={<CreditCard size={18} />}
          >
            {isSubmitting ? 'Processing...' : 'Complete Purchase'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TicketPurchase;